import { useEffect, useMemo, useState } from "react";
import { Building2, HardHat, Home } from "lucide-react";

import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

const TIPOS = [
  { id: "vivienda", icon: Home, titulo: "Vivienda", descripcion: "Piso, casa, unifamiliar" },
  { id: "local_comercial", icon: Building2, titulo: "Local comercial", descripcion: "Oficina, tienda, restaurante" },
  { id: "rehabilitacion_parcial", icon: HardHat, titulo: "Rehabilitación parcial", descripcion: "Fachada, cubierta, instalación" },
];

const ACABADOS = [
  { id: "basica", titulo: "Básica", desc: "Para alquiler o reformas económicas" },
  { id: "media", titulo: "Media", desc: "El estándar para vivir cómodamente" },
  { id: "alta", titulo: "Alta", desc: "Para clientes exigentes o lujo" },
];

const SUBTIPOS_VIVIENDA = ["Piso", "Casa adosada", "Unifamiliar", "Dúplex", "Ático", "Loft"];
const SUBTIPOS_LOCAL = ["Oficina", "Tienda", "Restaurante", "Bar/Cafetería", "Almacén", "Otro"];
const ZONAS_REHAB = ["Fachada", "Cubierta", "Instalación eléctrica", "Instalación de fontanería", "Instalación de climatización", "Estructura", "Otro"];

const INSTALACIONES_ESPECIALES = [
  "Climatización industrial",
  "Extracción de humos",
  "Instalación contra incendios",
  "Accesibilidad",
];

const PLANTAS = [
  { id: "1", label: "1 planta" },
  { id: "2", label: "2 plantas" },
  { id: "3", label: "3 plantas" },
  { id: "4+", label: "4 o más plantas" },
];

const DEFAULT_DETALLES = {
  subtipoVivienda: null,
  dormitorios: "",
  banos: "",
  subtipoLocal: null,
  aseos: "0",
  cocinaIndustrial: false,
  instalacionesEspeciales: [],
  zonaRehabilitacion: null,
  superficieAfectada: "",
};

const DEFAULT_DATOS = {
  tipoProyecto: null,
  obra: "reforma_integral",
  superficie: "",
  numPlantas: "1",
  nivelAcabados: "media",
  detalles: { ...DEFAULT_DETALLES },
  notasAdicionales: "",
};

export default function ProyectoIntegralFormModal({
  open,
  onClose,
  onVolver,
  onSubmit,
  datosIniciales,
  loading,
}) {
  const [datos, setDatos] = useState(() => mergeDatos(datosIniciales));

  useEffect(() => {
    if (open) setDatos(mergeDatos(datosIniciales));
  }, [open, datosIniciales]);

  const tipoInfo = useMemo(
    () => TIPOS.find((t) => t.id === datos.tipoProyecto) ?? null,
    [datos.tipoProyecto],
  );

  const updateDatos = (patch) => setDatos((d) => ({ ...d, ...patch }));
  const updateDetalles = (patch) =>
    setDatos((d) => ({ ...d, detalles: { ...d.detalles, ...patch } }));

  const toggleInstalacion = (nombre) => {
    setDatos((d) => {
      const set = new Set(d.detalles.instalacionesEspeciales ?? []);
      if (set.has(nombre)) set.delete(nombre);
      else set.add(nombre);
      return {
        ...d,
        detalles: { ...d.detalles, instalacionesEspeciales: Array.from(set) },
      };
    });
  };

  const handleSubmit = () => {
    const payload = {
      ...datos,
      superficie: numeroOpcional(datos.superficie),
      numPlantas: datos.numPlantas ?? null,
      detalles: {
        ...datos.detalles,
        dormitorios: numeroOpcional(datos.detalles.dormitorios),
        banos: numeroOpcional(datos.detalles.banos),
        aseos: numeroOpcional(datos.detalles.aseos),
        superficieAfectada: numeroOpcional(datos.detalles.superficieAfectada),
      },
    };
    onSubmit?.(payload);
  };

  const puedeEnviar =
    !!datos.tipoProyecto &&
    !!datos.obra &&
    !!datos.nivelAcabados &&
    Number(datos.superficie) > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="proyecto-integral-form-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-blue-50 text-blue-600">
              <Building2 className="w-4 h-4" />
            </span>
            <DialogTitle className="font-display font-bold text-lg">
              Proyecto integral
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-slate-500">
            Describe la reforma completa. La IA dividirá por zonas y revisará el
            resultado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">
              ¿Qué tipo de proyecto es?
            </div>
            <div className="grid grid-cols-3 gap-3">
              {TIPOS.map((t) => {
                const Icon = t.icon;
                const active = datos.tipoProyecto === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => updateDatos({ tipoProyecto: t.id })}
                    data-testid={`pi-form-tipo-${t.id}`}
                    className={`text-center p-3 rounded-md border transition-all flex flex-col items-center gap-1 ${
                      active
                        ? "border-blue-600 bg-blue-50/60 ring-2 ring-blue-600 text-blue-700"
                        : "border-slate-200 hover:border-slate-300 text-slate-700"
                    }`}
                  >
                    <Icon className="w-5 h-5 mb-0.5" />
                    <div className="font-display font-bold text-sm">{t.titulo}</div>
                    <div className="text-[11px] text-slate-500 leading-snug">
                      {t.descripcion}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">
              ¿Obra nueva o reforma?
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ToggleCard
                active={datos.obra === "reforma_integral"}
                onClick={() => updateDatos({ obra: "reforma_integral" })}
                label="Reforma integral"
                testid="pi-obra-reforma"
              />
              <ToggleCard
                active={datos.obra === "obra_nueva"}
                onClick={() => updateDatos({ obra: "obra_nueva" })}
                label="Obra nueva"
                testid="pi-obra-nueva"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
                ¿Cuántos m² tiene?
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={datos.superficie ?? ""}
                  onChange={(e) => updateDatos({ superficie: e.target.value })}
                  placeholder="Ej: 120"
                  data-testid="pi-superficie"
                  className="pr-10 tabular-nums"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                  m²
                </span>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
                Nº de plantas
              </Label>
              <Select
                value={datos.numPlantas ?? ""}
                onValueChange={(v) => updateDatos({ numPlantas: v })}
              >
                <SelectTrigger data-testid="pi-plantas">
                  <SelectValue placeholder="1 planta" />
                </SelectTrigger>
                <SelectContent>
                  {PLANTAS.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">
              ¿Qué nivel de acabados?
            </div>
            <div className="grid grid-cols-3 gap-3">
              {ACABADOS.map((a) => {
                const active = datos.nivelAcabados === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => updateDatos({ nivelAcabados: a.id })}
                    data-testid={`pi-acabado-${a.id}`}
                    className={`text-center p-3 rounded-md border transition-all flex flex-col items-center gap-0.5 ${
                      active
                        ? "border-blue-600 bg-blue-50/60 ring-2 ring-blue-600 text-blue-700"
                        : "border-slate-200 hover:border-slate-300 text-slate-700"
                    }`}
                  >
                    <div className="font-display font-bold text-sm">{a.titulo}</div>
                    <div className="text-[11px] text-slate-500 leading-snug">
                      {a.desc}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {tipoInfo && (
            <div className="space-y-3 pt-1 border-t border-slate-100">
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500 pt-3">
                Detalles {labelDetalles(datos.tipoProyecto)}
              </div>

              {datos.tipoProyecto === "vivienda" && (
                <div className="space-y-3">
                  <SelectField
                    label="Subtipo de vivienda"
                    value={datos.detalles.subtipoVivienda}
                    onChange={(v) => updateDetalles({ subtipoVivienda: v })}
                    placeholder="Selecciona…"
                    options={SUBTIPOS_VIVIENDA}
                    testid="pi-subtipo-vivienda"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <NumberField
                      label="Nº de dormitorios"
                      value={datos.detalles.dormitorios ?? ""}
                      onChange={(v) => updateDetalles({ dormitorios: v })}
                      testid="pi-dormitorios"
                    />
                    <NumberField
                      label="Nº de baños"
                      value={datos.detalles.banos ?? ""}
                      onChange={(v) => updateDetalles({ banos: v })}
                      testid="pi-banos"
                    />
                  </div>
                </div>
              )}

              {datos.tipoProyecto === "local_comercial" && (
                <div className="space-y-3">
                  <SelectField
                    label="Subtipo de local"
                    value={datos.detalles.subtipoLocal}
                    onChange={(v) => updateDetalles({ subtipoLocal: v })}
                    placeholder="Selecciona…"
                    options={SUBTIPOS_LOCAL}
                    testid="pi-subtipo-local"
                  />
                  <NumberField
                    label="Aseos"
                    value={datos.detalles.aseos ?? ""}
                    onChange={(v) => updateDetalles({ aseos: v })}
                    testid="pi-aseos"
                  />
                  <label
                    className="flex items-center gap-2 text-sm text-slate-700 select-none"
                    data-testid="pi-cocina-industrial-wrapper"
                  >
                    <Checkbox
                      checked={datos.detalles.cocinaIndustrial}
                      onCheckedChange={(v) =>
                        updateDetalles({ cocinaIndustrial: !!v })
                      }
                      data-testid="pi-cocina-industrial"
                    />
                    Necesita cocina industrial
                  </label>
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
                      Instalaciones especiales
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {INSTALACIONES_ESPECIALES.map((inst) => {
                        const active = (
                          datos.detalles.instalacionesEspeciales ?? []
                        ).includes(inst);
                        return (
                          <button
                            key={inst}
                            type="button"
                            onClick={() => toggleInstalacion(inst)}
                            data-testid={`pi-instalacion-${slug(inst)}`}
                            className={`px-3 py-1 rounded-full border text-xs transition-colors ${
                              active
                                ? "border-blue-600 bg-blue-50 text-blue-700"
                                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                            }`}
                          >
                            {inst}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {datos.tipoProyecto === "rehabilitacion_parcial" && (
                <div className="space-y-3">
                  <SelectField
                    label="Zona o elemento"
                    value={datos.detalles.zonaRehabilitacion}
                    onChange={(v) => updateDetalles({ zonaRehabilitacion: v })}
                    placeholder="Selecciona…"
                    options={ZONAS_REHAB}
                    testid="pi-zona-rehab"
                  />
                  <NumberField
                    label="Superficie afectada (m²)"
                    value={datos.detalles.superficieAfectada ?? ""}
                    onChange={(v) => updateDetalles({ superficieAfectada: v })}
                    testid="pi-superficie-afectada"
                  />
                </div>
              )}
            </div>
          )}

          <div className="pt-3 border-t border-slate-100">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
              Detalles adicionales <span className="text-slate-400">(opcional)</span>
            </Label>
            <Textarea
              rows={3}
              value={datos.notasAdicionales ?? ""}
              onChange={(e) =>
                updateDatos({ notasAdicionales: e.target.value })
              }
              placeholder="Particularidades del proyecto, restricciones, plazos, etc."
              data-testid="pi-notas"
            />
          </div>
        </div>

        <div className="flex justify-between gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onVolver?.()}
            disabled={loading}
            className="border-slate-300"
            data-testid="pi-form-volver"
          >
            Atrás
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onClose?.()}
              disabled={loading}
              className="border-slate-300"
              data-testid="pi-form-cancelar"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!puedeEnviar || loading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="pi-form-continuar"
            >
              Continuar al prompt
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToggleCard({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-3 rounded-md border text-sm font-medium transition-all ${
        active
          ? "border-blue-600 bg-blue-50/60 ring-2 ring-blue-600 text-blue-700"
          : "border-slate-200 hover:border-slate-300 text-slate-700"
      }`}
    >
      {label}
    </button>
  );
}

function SelectField({ label, value, onChange, placeholder, options, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
        {label}
      </Label>
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
        {label}
      </Label>
      <Input
        type="number"
        min="0"
        step="1"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="tabular-nums"
      />
    </div>
  );
}

function labelDetalles(tipo) {
  if (tipo === "vivienda") return "de la vivienda";
  if (tipo === "local_comercial") return "del local";
  if (tipo === "rehabilitacion_parcial") return "de la rehabilitación";
  return "";
}

function numeroOpcional(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function slug(s) {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function mergeDatos(d) {
  if (!d) return { ...DEFAULT_DATOS, detalles: { ...DEFAULT_DETALLES } };
  return {
    ...DEFAULT_DATOS,
    ...d,
    detalles: { ...DEFAULT_DETALLES, ...(d.detalles ?? {}) },
  };
}
